import { DOCUMENT, isPlatformBrowser, isPlatformServer, Location, PlatformLocation } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ListContainer } from '@app/core/interfaces/list/list-container';
import { ListInterface } from '@app/core/interfaces/list/list.interface';
import { BlockActionType, clientPageTypeEnum, LIST_PAGE_TYPES, MAIN_PAGE_TYPE, PREVIEW_PAGE_TYPES } from '@core/enums';
import {
  isListOnCurrentPage,
  LIST_CONTEXT_STORAGE_KEY,
  ListContextEntry,
  parseListContextStore,
  withListContextEntry,
} from '@modules/main/helpers/list-context.helper';
import {
  readViewNameFromParams,
  resolveRequestTypeView,
} from '@modules/creator/components/special-block/list/helpers/list-view-params.helper';
import { LoaderInterface } from '@core/interfaces/loader.interface';
import { MixedObject } from '@core/interfaces/mixed-object';
import { Style } from '@core/interfaces/styles/styles.interface';

import { BlockAction, BlockSettings, FilterType, ListType, PaginationType, TableParams } from '@core/models';
import { Block } from '@core/models/block/block.model';
import { ApiDataService } from '@core/services/api/api-data.service';
import { ButtonHandlerService } from '@core/services/button-handler.service';
import { ClientService } from '@core/services/client/client.service';
import { CurrencyPreferenceService } from '@shared/services/currency/currency-preference.service';
import { GlobalService } from '@core/services/common/global.service';
import { MediaQueryBuilder } from '@core/services/common/styles/media-query-builder';
import { StyleService } from '@core/services/common/styles/style.service';

import { SubSink } from '@core/utils/subsink';
import { LoaderService } from '@modules/loaders/services/loader.service';
import { chunkArray, encodeString, isFullyVisible, objectKeysToSelectedCase } from '@shared/helpers';
import { findAllByActionType, findFirstByActionTypeAndData } from '@shared/helpers/block-helpers';
import { focusBody } from '@shared/helpers/dom-helpers';
import { findElementByBlockIdRecursiveDown } from '@shared/helpers/ui-helpers';
import { createTypesRecursive } from '@shared/services/factories/block-factory.service';
import { AdvancedFiltersService } from '@shared/services/filters/advanced-filters.service';
import { FiltersService } from '@shared/services/filters/filters.service';
import { ListDetailsService } from '@shared/services/list/list-details.service';
import { LocalStorageService, SessionStorageService } from '@shared/services/storage';
import { camelCase } from 'lodash';

import { BehaviorSubject } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';

export interface ListParams {
  path: string;
  page: number;
  scrollTopIdElement: string;
  data: ListInterface;
}

@Component({
  selector: 'app-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  standalone: false,
})
export class ListComponent implements OnInit, OnDestroy {
  @Input() specialBlock: Block;

  @ViewChild('listContainer') listContainer: ElementRef;
  @ViewChild('filtersContainer') filtersContainer: ElementRef;
  @ViewChild('sortContainer') sortContainer: ElementRef;

  settings: BlockSettings;
  structure: ListContainer[];
  filters: Block[];
  sorting: Block[];
  pagination: Block[];
  header: Block[];
  noResults: Block[];
  tabs: Block[];
  activeTab: string;
  loading = new BehaviorSubject<boolean>(false);
  loadingFilters = new BehaviorSubject<boolean>(false);
  showMainLoader = false;
  masonryList: Record<string, Block[][][]> = {};

  private _allItemsCount: number;
  private get allItemsCount(): number {
    return this._allItemsCount;
  }

  private set allItemsCount(val: number) {
    this._allItemsCount = val;
    ListDetailsService.updateCount(val);
  }

  private allPages: any;
  private typePaginator = true;
  private data: any[];
  private paginatorOn = false;
  private listPathName = this.document.location.pathname;
  private scrollTopIdElement = null;
  private queryParams: Record<string, string>;
  private advancedFilterParams: Params = {};
  private subSink = new SubSink();
  private initialized = false;
  private _allListDivs: NodeListOf<any>;
  private currentViewBlockStyle: Style = {};
  private advancedFiltersService: AdvancedFiltersService;
  private currencyPreferenceService: CurrencyPreferenceService;

  constructor(
    private styleService: StyleService,
    private clientService: ClientService,
    private apiService: ApiDataService,
    private buttonHandlerService: ButtonHandlerService,
    private location: Location,
    platformLocation: PlatformLocation,
    private route: ActivatedRoute,
    private router: Router,
    private localStorageService: LocalStorageService,
    private storageService: SessionStorageService,
    private renderer: Renderer2,
    public loaderService: LoaderService,
    private globalService: GlobalService,
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(DOCUMENT) private document: Document,
    @Optional() private filtersService: FiltersService,
  ) {
    this.advancedFiltersService = inject(AdvancedFiltersService);
    this.currencyPreferenceService = inject(CurrencyPreferenceService);
    platformLocation.onPopState(() => {
      this.isRouteBack = true;
    });

    this.subSink.sink = loaderService.loader.subscribe(
      (result: LoaderInterface) => {
        this.showMainLoader = result.display;
      },
    );
  }

  get isMasonryList(): boolean {
    return this.settings?.listType === ListType.MASONRY;
  }

  get listContainerClass() {
    return {
      [this.specialBlock.uId]: true,
      masonry: this.isMasonryList,
    };
  }

  get listContainerStyle() {
    const {columnGap, gridTemplateColumns} = this.currentViewBlockStyle;

    const numberOfColumns = gridTemplateColumns.split(' ').length;
    const columnGapInt = columnGap
      ? parseInt(columnGap.replace('px', ''), 10)
      : 0;
    const calculatedSpace = Math.floor(
      columnGapInt - columnGapInt * (1 / numberOfColumns),
    );

    const itemWidth =
      calculatedSpace > 0
        ? `calc((100% / ${numberOfColumns}) - ${calculatedSpace}px)`
        : `calc(100% / ${numberOfColumns})`;

    this.globalService.numberOfColumnsInTheList = numberOfColumns;
    return {
      gridTemplateColumns: `${itemWidth} `.repeat(numberOfColumns).trim(),
    };
  }

  _page = 1;

  get page(): number {
    return this._page;
  }

  set page(page: number) {
    this._page = page && page >= 1 ? page : 1;
  }

  /**
   * Nazwy widoków, które lista rozpoznaje jako kategorię wyników w adresie.
   * Rejestr trzyma `GlobalService`, bo to on pamięta numer strony każdej kategorii
   * przy przełączaniu zakładek wyszukiwarki globalnej.
   */
  private get knownViewNames(): string[] {
    return Object.keys(this.globalService.paramsGlobalSearch);
  }

  get isListPageType(): boolean {
    return LIST_PAGE_TYPES.includes(
      this.route.snapshot.data.pageData?.page?.type,
    );
  }

  get isMainPageType(): boolean {
    return MAIN_PAGE_TYPE.includes(
      this.route.snapshot.data.pageData?.page?.type,
    );
  }

  get isPreviewPageType(): boolean {
    return PREVIEW_PAGE_TYPES.includes(
      this.route.snapshot.data.pageData?.page?.type,
    );
  }

  get isCmsPage(): boolean {
    return (
      clientPageTypeEnum.UNIVERSAL_TYPE ===
      this.route.snapshot.data.pageData?.page?.type
    );
  }

  get showFilters(): boolean {
    return (
      !!this.settings.showFilters &&
      this.validshowMobileFilterView() &&
      (this.isMainPageType ||
        this.isListPageType ||
        this.isPreviewPageType ||
        this.isCmsPage)
    );
  }

  _showPagination = true;

  get showPagination(): boolean {
    return (
      this._showPagination &&
      (this.isMainPageType ||
        this.isListPageType ||
        this.isPreviewPageType ||
        this.isCmsPage)
    );
  }

  get isRouteBack(): boolean {
    return !!this.localStorageService.getItem('isRouteBack') || false;
  }

  set isRouteBack(data: boolean) {
    const value = data ? 1 : 0;
    this.localStorageService.setItem('isRouteBack', `${value}`);
  }

  get isFiltersBlockOnLeft(): boolean {
    const {showFilters, filtersPosition} = this.settings;
    return (
      showFilters &&
      filtersPosition === 'left' &&
      this.validTopMobileFilterView()
    );
  }

  get liatContainerClass(): MixedObject {
    return {
      'filters-left': this.isFiltersBlockOnLeft,
    };
  }

  ngOnInit(): void {
    this.settings = this.specialBlock.settings;

    this.initializeHandlersForMediaQuery();

    this.initializeHandlers();
    this.initializeFilterChanging();

    this.route.queryParamMap.subscribe((params) => {
      const page = +params.get('pageNumber');
      this.page = page ? page : 1;
    });

    this.advancedFiltersService.setInitialListParams(this.settings.queryParams);

    this.advancedFiltersService.paramsForListUpdated$.subscribe((params) => {
      this.advancedFiltersChanged(params);
    });

    // po stronie serwera nie renderujemy listy
    if (isPlatformServer(this.platformId)) {
      return;
    }

    // AT-9152: wpis nalezy do strony, na ktorej lista faktycznie stoi - lista osadzona
    // w podgladzie nie moze nadpisywac wpisu strony listy tego samego typu.
    this.saveListUrlToSessionStorage();

    if (!this.data || this.data.length) {
      this.loading.next(true);
      // Stan filtrów zaawansowanych może już istnieć (powrót z podglądu pracy
      // albo odtworzenie po odświeżeniu strony) — pierwszy request listy
      // musi go uwzględnić, inaczej liczba prac nie zgadzałaby się z filtrami.
      this.advancedFilterParams = this.getQueryParamsWithoutUnnecessaryFields(
        this.advancedFiltersService.getActiveFiltersValue(),
      );
      const tableParams = Object.keys(this.advancedFilterParams).length
        ? this.getTableParamsWithoutBListBaseParams(this.getListRequestParams(this.queryParams))
        : this.getTableParams(this.queryParams);
      this.filtersService.paramsSelectFiters = tableParams;
      const {scrollTopIdElement, data} = this.getCurrentListParams();
      this.allPages = 30;
      if (data?.elements) {
        this.afterGetData(this.mapListData(data), scrollTopIdElement);
      } else {
        this.subSink.sink = this.apiService
          .getList(tableParams)
          .subscribe((list: any) => {
            this.filtersService.listCategory.next(list.listCategoryFilter);
            this.filtersService.listFilterSlidable.next(list);
            const numPageLength = list?.elements[0]?.data.length
              ? list?.elements[0]?.data.length
              : 0;
            if (numPageLength > 0) {
              this.allPages = list.allCount / list.settings.elementsPerPage;

              if (list.pagination?.[0]?.settings.paginationType === PaginationType.STANDARD) {
                this.typePaginator = false;
              }
              if (!list.allCount) {
                this.allPages = +list.elements[0].allCount / list.settings.elementsPerPage;
              }
            } else {
              this.typePaginator = false;
              this.allPages = 0;
            }
            this.mapListData(list);
            this.makeListTabActive(list);
            this.afterGetData(list, scrollTopIdElement);
            this.refreshDivPaginator();
          });
      }
      this.initialized = true;
    }
  }

  onUpdateFiltersContent() {
    if (this.isFiltersBlockOnLeft && this.filtersContainer) {
      const filtersBlockWidth =
        this.filtersContainer.nativeElement.getBoundingClientRect().width;
      this.listContainer?.nativeElement.style.setProperty(
        'width',
        `calc(100% - ${filtersBlockWidth}px)`,
      );
    } else {
      this.listContainer?.nativeElement.style.removeProperty('width');
    }
  }

  ngOnDestroy(): void {
    this.scrollTopIdElement =
      this.getFirstVisibleElementOnScreen()?.getAttribute('id') || null;
    this.setCurrentListParams();
    this.subSink.unsubscribe();
  }

  validTopMobileFilterView() {
    const windowWidth: number = window.innerWidth;
    return windowWidth <= 992 ? !this.settings.topMobileViewFilters : true;
  }

  validshowMobileFilterView() {
    const windowWidth: number = window.innerWidth;
    return windowWidth <= 992 ? !this.settings.showMobileViewFilters : true;
  }

  getPaginator(structure, data) {
    let pagination = [];
    structure?.forEach((item) => {
      if (item.viewName == this.activeTab) {
        this.pagination = item.pagination;
        pagination = item.pagination;
        if (item.allCount) {
          ListDetailsService.objectCountOnList.next(item.allCount);
          this.allPages = +item?.allCount / +data.settings.elementsPerPage;
        }
      }
    });
    if (pagination.length == 0) {
      this.pagination = data.pagination;
      pagination = data.pagination;
    }
    return pagination;
  }

  validPaginator(): boolean {
    return (
      (this.showPagination || !this.loading) &&
      (this.allPages > 1 || this.typePaginator)
      && !this.noResults?.length
    );
  }

  private mapListData(list: ListInterface): ListInterface {
    list.filters = createTypesRecursive(list.filters);
    list.pagination = createTypesRecursive(list.pagination);
    list.sorting = createTypesRecursive(list.sorting);
    list.noResults = createTypesRecursive(list.noResults);
    list.elements = list.elements.map((view) => {
      view.data = view.data.map((itemBlocks) =>
        createTypesRecursive(itemBlocks),
      );
      view.pagination = createTypesRecursive(view?.pagination);
      if (view?.viewName === this.activeTab) {
        this.allPages = +view?.allCount / list.settings.elementsPerPage;
      }
      return view;
    });

    list.tabs = list.tabs ? createTypesRecursive(list.tabs) : [];
    return list;
  }

  private setCurrentViewBlockStyle(query: string) {
    const styles = this.specialBlock.styles;
    let connectedStyle = styles.all?.basic || {};
    if (query === GlobalService.MOBILE) {
      connectedStyle = {
        ...connectedStyle,
        ...(styles[camelCase(GlobalService.TABLET)]?.basic || {}),
        ...(styles[camelCase(GlobalService.MOBILE)]?.basic || {}),
      };
    }
    if (query === GlobalService.TABLET) {
      connectedStyle = {
        ...connectedStyle,
        ...(styles[camelCase(GlobalService.TABLET)]?.basic || {}),
      };
    }
    this.currentViewBlockStyle = connectedStyle;
  }

  private refreshDivPaginator(): void {
    setTimeout(() => {
      this._showPagination = true;
    }, 100);
  }

  private updateStructure(elements: ListContainer[]) {
    this.structure = elements;
  }

  private addToStructure(elements: ListContainer[]) {
    this.structure.push(...elements);
  }

  private addToStructureFirstView(elements: ListContainer[]) {
    if (elements[0]?.data) {
      const data = elements[0]?.data;
      this.structure[0]?.data.push(...data);
    }
  }

  private updateListStructure(data: ListInterface | null = null): void {
    this.updateStructure(data?.elements);
    this.noResults = data?.noResults;
    if (!this.filters) {
      this.filters = data?.filters;
    }
    if (!this.sorting) {
      this.sorting = data?.sorting;
    }
    this.tabs = data?.tabs;
    this.pagination = this.getPaginator(data?.elements, data);

    // Check if header content has changed before updating
    const headerContent = data?.header && data.header.length > 0 ? data.header[0]?.content : null;
    const currentHeaderContent = this.header && this.header.length > 0 ? this.header[0]?.content : null;

    if (headerContent !== currentHeaderContent) {
      this.header = data?.header;
    }
    if (data?.allCount !== undefined) {
      this.allItemsCount = data?.allCount;
      this.allPages = +data?.allCount / +data.settings.elementsPerPage;
    }
    if (this.isMasonryList) {
      this._generateMasonryLayout();
    }

  }

  private getTableParams = (params: Record<string, string>): TableParams => {
    const {id} = this.clientService.client;
    const {defaultSorting, elementsPerPage, queryParams} = this.settings;
    const sorting = defaultSorting.split('_');
    const paramsFiltered: any = this.getQueryParamsWithoutUnnecessaryFields(queryParams);
    // Convert query parameters to snake_case for backend compatibility
    const objectKeysResult = objectKeysToSelectedCase(
      paramsFiltered,
      'snake',
      false,
      true,
    );

    // Create table parameters for API request
    const tableParams = new TableParams({
      limit: elementsPerPage,
      column: sorting[0],
      direction: sorting[1],
      clientId: id,
      specialBlockId: this.specialBlock.id,
      pageUrl: this.listPathName,
      ...objectKeysResult,
      ...params, // URL parameters override default settings
      pageNumber: this.page,
      typeView: resolveRequestTypeView(this.activeTab, params, this.knownViewNames),
    });

    this.saveQueryParamsToSessionStorage(tableParams);

    return tableParams;
  };
  private getTableParamsWithoutBListBaseParams = (params: Record<string, string>): TableParams => {
    const {id} = this.clientService.client;
    const {defaultSorting, elementsPerPage} = this.settings;
    const sorting = defaultSorting.split('_');
    params = this.getQueryParamsWithoutUnnecessaryFields(params);

    const tableParams = new TableParams({
      limit: elementsPerPage,
      column: sorting[0],
      direction: sorting[1],
      clientId: id,
      specialBlockId: this.specialBlock.id,
      pageUrl: this.listPathName,
      ...params, // URL parameters override default settings
      pageNumber: this.page,
      typeView: resolveRequestTypeView(this.activeTab, params, this.knownViewNames),
    });

    this.saveQueryParamsToSessionStorage(tableParams);

    return tableParams;
  };

  private saveListUrlToSessionStorage(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.updateListContext({listUrl: this.listPathName});
  }

  private saveQueryParamsToSessionStorage(tableParams: TableParams): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const urlParams = new TableParams(tableParams);
    urlParams.delete('pageNumber');
    const plainObj = urlParams.toPlainObject();

    // Stan URL w chwili zapisu (po ewentualnym Location.go() z paginacji/filtrów),
    // nie zamrożony zrzut z ngOnInit — inaczej nadpisywałby świeże parametry listy.
    const listUrlSearch = window.location.search;
    if (listUrlSearch) {
      const params = new URLSearchParams(listUrlSearch);

      const obj = {};
      params.forEach((value, key) => {
        obj[key] = value.includes(',') ? value.split(',') : value;
      });

      Object.assign(plainObj, obj);
    }

    /*
     * AT-9249: currency nie jest częścią tableParams (dokładana dopiero do adresu
     * żądania listy, appendCurrencyToUrlIfSwitcherExists) i po pierwszej nawigacji
     * routerem znika z window.location.search — bez tego zapis milczy o wybranej
     * walucie, a sąsiedzi w podglądzie liczą się po walucie domyślnej klienta.
     * withCurrencyParamIfSwitcherExists tak jak wszędzie indziej dokłada ją tylko,
     * gdy backend potwierdził istnienie komponentu zmiany waluty.
     */
    const currency = this.currencyPreferenceService
      .withCurrencyParamIfSwitcherExists(new URLSearchParams())
      .get('currency');
    if (currency) {
      plainObj['currency'] = currency;
    }

    const listQueryParams = {
      listQueryParams: encodeString(JSON.stringify(plainObj)),
      listUrl: this.listPathName,
    };

    this.updateListContext({listQueryParams: encodeString(JSON.stringify(listQueryParams))});
  }

  /**
   * AT-9152: kontekst listy kubelkowany po sciezce strony, do ktorej lista nalezy.
   * Wczesniej byl jeden globalny wpis na typ bloku, wiec lista wystaw osadzona
   * w podgladzie pracy nadpisywala wpis strony listy wystaw i nawigacja sasiadow
   * dostawala parametry zupelnie innej listy.
   *
   * AT-8900: strona wpisu to strona listy (`listPathName` z chwili utworzenia
   * komponentu), nie biezaca lokalizacja. Przy zejsciu do podgladu ta lista jeszcze
   * pracuje - konczy `ngOnInit` i reaguje na pusty zestaw parametrow nowego adresu -
   * a router przestawil juz adres, wiec zapis kluczowany lokalizacja dopisywal wpis
   * listy prac na stronie podgladu, w dodatku z domyslnymi parametrami bloku. Taki
   * wpis czynil podglad zrodlem nawigacji sasiadow i po pierwszym skoku
   * Poprzedni/Nastepny gubila filtry, wyszukiwanie i strone listy.
   */
  private updateListContext(entry: Partial<ListContextEntry>): void {
    if (!isListOnCurrentPage(this.listPathName, window.location.pathname)) {
      return;
    }

    const store = parseListContextStore(this.storageService.getItem(LIST_CONTEXT_STORAGE_KEY));
    const updated = withListContextEntry(
      store,
      this.listPathName,
      this.specialBlock.type,
      entry,
    );

    this.storageService.setItem(LIST_CONTEXT_STORAGE_KEY, JSON.stringify(updated));
  }

  private validType(type: string): boolean {
    return (
      (+type && typeof +type === 'number' && +type > 0) ||
      type === 'BEFORE_PAGE' ||
      type === 'NEXT_PAGE'
    );
  }

  private initializeHandlersForMediaQuery(): void {
    this.subSink.sink = this.globalService.pageWidthSubject
      .pipe(filter(() => !!this.specialBlock))
      .subscribe(() => {
        this.setCurrentViewBlockStyle(
          this.globalService.mediaQuery.value.query,
        );
        setTimeout(() => this.onUpdateFiltersContent(), 1);
      });

    this.subSink.sink = this.globalService.mediaQuery
      .pipe(filter(() => !!this.specialBlock))
      .subscribe(({query}) => {
        this.setCurrentViewBlockStyle(query);
      });
  }

  private initializeHandlers(): void {
    this.paginatorOn = true;
    this.subSink.sink = this.buttonHandlerService.click$
      .pipe(
        filter((action: BlockAction) => {
          return (
            action.actionType === BlockActionType.GET &&
            action.actionData &&
            (action.actionData.type === 'LOAD_MORE' ||
              this.validType(action.actionData.type))
          );
        }),
        tap((action: BlockAction) => {
          if (action.actionData.type !== 'LOAD_MORE') {
            if (isPlatformBrowser(this.platformId)) {
              setTimeout(() => {
                window.scrollTo({top: 0, behavior: 'smooth'});
              }, 100);
            }
          }
        }),
      )
      .subscribe((data) => {
        this.loadMoreData(data);
      });
    this.subSink.sink = this.buttonHandlerService.click$
      .pipe(
        filter((action: BlockAction) => {
          if (action.actionType === BlockActionType.CLEAR) {
            this.filtersService.paramsGlobalClean = true;
          }
          return (
            action.actionType === BlockActionType.CHANGE_VIEW &&
            action.actionData &&
            action.actionData.viewName
          );
        }),
      )
      .subscribe(({actionData: {viewName}}) => {
        this.removeSpecificParams(viewName);
        return this.setActiveTab(viewName, this.globalService.dataPaginator);
      });

    this.subSink.sink = this.route.queryParams.subscribe((params) => {
      return this.updateQueryParams(params);
    });
  }

  private updateQueryParams(params: Params): void {
    this.queryParams = params;
    if (params.pageNumber) {
      this.page = params.pageNumber;
    }

    // Wybrana kategoria wyników jest w adresie na równi z numerem strony, więc odczyt
    // musi być symetryczny do zapisu — inaczej wejście pod zapamiętany adres listy
    // (powrót do listy, udostępniony odnośnik) pokazuje pierwszą kategorię i pierwszą
    // stronę, bo żądanie idzie do backendu bez widoku.
    const viewNameFromUrl = readViewNameFromParams(params, this.knownViewNames);
    if (viewNameFromUrl) {
      this.activeTab = viewNameFromUrl;
    }

    // Update filters service with new parameters and trigger data refresh if component is initialized
    this.filtersService?.updateFiltersFrom(params);
    if (this.initialized) {
      this.loadingFilters.next(true);
      this.updateList(this.queryParams);
      this.changePaginationVisibility();
    }
  }

  private advancedFiltersChanged(params: Params): void {
    this.page = 1;
    this.advancedFilterParams = this.getQueryParamsWithoutUnnecessaryFields(params);

    if (this.initialized) {
      this.loadingFilters.next(true);
      this.updateListAfterAdvancedFiltersChanged(this.getListRequestParams(this.queryParams));
      this.changePaginationVisibility();
    }
  }

  private afterGetData(
    data: ListInterface,
    scrollTopIdElement: any | null = null,
  ): void {
    this.globalService.dataPaginator = data;
    this.updateListStructure(data);
    this.makeListTabActive(data);
    this.filtersService.listCategory.next(data.listCategoryFilter);

    this.filtersService.listFilterSlidable.next([data]);
    this.filtersService.paramsGlobalClean = false;

    // Force set loading to false to enable filter fields
    ListDetailsService.setLoading(false);

    // zanim wyświetlimy dane dodajemy style
    this.setStyles(data);

    this.changePaginationVisibility();
    this.loading.next(false);
    this.loadingFilters.next(false);
    this._allListDivs = document.querySelectorAll('.list-container .list-item');
    if (data.allCount !== undefined) {
      this.allItemsCount = data?.allCount;
      this.allPages = +data?.allCount / +data.settings.elementsPerPage;
    }

    // this.generateMasonryLayout();
    this.scrollTo(scrollTopIdElement);
    this.globalService.runScrollHandler();
  }

  private setStyles(data: ListInterface): void {
    // pagination styles
    const paginationStyles = MediaQueryBuilder.build(this.pagination);
    StyleService.bodyStyles.push(paginationStyles);

    // filter styles
    const filterStyles = MediaQueryBuilder.build(data.filters);
    StyleService.bodyStyles.push(filterStyles);

    // sorting styles nadpisujemy style bloku sortowania
    this.addTrylToViewPartSortingContent1(data.header);
    this.addTrylToViewPartSortingContent1(data.sorting);

    const sortingrStyles = MediaQueryBuilder.build(data.sorting);
    StyleService.bodyStyles.push(sortingrStyles);

    // header styles
    const headerStyles = MediaQueryBuilder.build(data.header);
    StyleService.bodyStyles.push(headerStyles);

    // this.specialBlockType(data.header);

    // no results style
    const noResultsStyles = MediaQueryBuilder.build(data.noResults);
    StyleService.bodyStyles.push(noResultsStyles);

    if (data.tabs) {
      const tabsStyles = MediaQueryBuilder.build(data.tabs);
      StyleService.bodyStyles.push(tabsStyles);
    }

    data.elements.forEach((elements) => {
      if (elements?.data) {
        this.createStyles(elements.data);
      }
    });

    // Pojedyncze wywołanie styleService.setStyles() po zebraniu wszystkich
    // fragmentów CSS (pagination, filters, sorting, header, noResults, tabs,
    // elementy listy) sprawia, że stary <style> w <head> jest podmieniany raz,
    // zamiast raz na każdą sekcję. Wywołanie sync-CSS + subskrypcja
    // ngZone.onStable są kosztowne, więc zamiast ich powielania spinamy
    // wynik w jedno przejście.
    this.styleService.setStyles();
  }

  addTrylToViewPartSortingContent1(data) {
    if (Array.isArray(data)) {
      data.forEach((item) => this.addTrylToViewPartSortingContent1(item));
    } else if (typeof data === 'object' && data !== null) {
      if (data.settings && data.settings.viewPart === 'sortingContent1') {
        data.tryl = true;
        data.styles.all.basic['display'] = 'none';
      }

      if (data.children && Array.isArray(data.children)) {
        data.children.forEach((child) =>
          this.addTrylToViewPartSortingContent1(child),
        );
      }

      if (data.specialBlock) {
        this.addTrylToViewPartSortingContent1(data.specialBlock);
      }
    }
  }

  private makeListTabActive(data: ListInterface): void {
    const elementsHasCurrentActiveTab = data.elements
      .map((el) => el.viewName)
      .includes(this.activeTab);
    if (this.activeTab && elementsHasCurrentActiveTab) {
      this.setActiveTab(this.activeTab, data);
    }
    if (
      (!this.activeTab || !elementsHasCurrentActiveTab) &&
      data.elements.length > 0
    ) {
      this.setActiveTab(data.elements[0]?.viewName, data);
    }
  }

  private setActiveTab(viewName: string, data?: ListInterface): void {
    this.activeTab = viewName;
    if (data?.elements) {
      const view = data.elements.find(el => el.viewName === viewName);
      this.allItemsCount = view.allCount || data.allCount;
      this.allPages = +this.allItemsCount / +data.settings.elementsPerPage;
    }

    this.getPaginator(data?.elements, data);
    this.setStyles(data);
    this.changePaginationVisibility();
    // this.mapListData(JSON.parse(localStorage.getItem('paramsPaginator')))
    if (this.tabs && this.tabs[0]) {
      setTimeout(() => {
        const foundTabBlockToActivate: Block | undefined =
          findFirstByActionTypeAndData(
            this.tabs[0],
            BlockActionType.CHANGE_VIEW,
            'viewName',
            viewName,
          );

        const allTabsBlocksUids: string[] = findAllByActionType(
          this.tabs[0],
          BlockActionType.CHANGE_VIEW,
        ).map((blk: Block) => blk.uId);
        allTabsBlocksUids.forEach((uid) => {
          const element = findElementByBlockIdRecursiveDown(
            this.listContainer.nativeElement,
            uid,
          );
          if (element) {
            this.renderer.removeClass(element.element, `${uid}__active`);
          }
        });
        if (!foundTabBlockToActivate) {
          return;
        }
        const elementToActivate = findElementByBlockIdRecursiveDown(
          this.listContainer.nativeElement,
          foundTabBlockToActivate.uId,
        );
        if (elementToActivate) {
          this.renderer.addClass(
            elementToActivate.element,
            `${foundTabBlockToActivate.uId}__active`,
          );
        }
      }, 50);
    }
  }

  private scrollTo(scrollTopIdElement: any): void {
    if (!scrollTopIdElement) {
      return;
    }
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        const element = document.getElementById(scrollTopIdElement);
        const {top} = element?.getBoundingClientRect() || {};
        // musze przeskrolować o przynajmniej 1px żeby angular złapał zmiany w stopce
        window.scrollTo({top: top || 1, behavior: 'smooth'});
      }, 600);
    }
  }

  private createStyles(matrix: Block[][]): void {
    // Zbieramy tylko fragmenty CSS do wspólnego bufora — wywołanie
    // `styleService.setStyles()`, które faktycznie podmienia <style> w <head>
    // i uruchamia subskrypcję `ngZone.onStable`, wykonuje strona wywołująca
    // po zebraniu wszystkich fragmentów całej odpowiedzi listy.
    for (const _blocks of matrix) {
      const blocksStyles = MediaQueryBuilder.build(_blocks);
      StyleService.bodyStyles.push(blocksStyles);
    }
  }

  private loadMoreData(type: BlockAction): void {
    if (type.actionData.type === 'LOAD_MORE') {
      this.page++;

      const url = this.router
        .createUrlTree([], {
          relativeTo: this.route,
          queryParams: {pageNumber: this.page, typeView: this.activeTab},
          queryParamsHandling: 'merge',
        })
        .toString();
      this.location.go(url);
      this.typePaginator = false;
      this.loading.next(true);

      const requestParams = this.getListRequestParams(this.queryParams);
      const tableParams = this.getTableParamsForListRequest(requestParams);
      this.subSink.sink = this.apiService
        .getList(tableParams)
        .subscribe((data) => {
          let numPageLength = data?.elements[0]?.data.length
            ? data?.elements[0]?.data.length
            : 0;
          if (numPageLength > 0) {
            data.elements.forEach((elements) =>
              this.createStyles(elements.data),
            );
            // Po zebraniu wszystkich fragmentów CSS jedno wspólne wywołanie
            // podmienia <style> w <head>; wcześniej createStyles robił to raz
            // na każdy rząd elementów.
            this.styleService.setStyles();
            if (this.structure[0] && data.elements[0]) {
              this.addToStructureFirstView(data.elements);
            } else {
              this.addToStructure(data.elements);
            }
            this.changePaginationVisibility();
            this.loading.next(false);
            focusBody();
            numPageLength = data?.elements[0]?.data.length
              ? data?.elements[0]?.data.length
              : 0;
            if (numPageLength > 0) {
              this.allPages = +data?.allCount / +data.settings.elementsPerPage;
            }
            if (!data.elements.length) {
              const urlBack = this.router
                .createUrlTree([], {
                  relativeTo: this.route,
                  queryParams: {
                    pageNumber: this.page - 1,
                    typeView: this.activeTab,
                  },
                  queryParamsHandling: 'merge',
                })
                .toString();
              this.location.go(urlBack);
            }
          }
          if (this.isMasonryList) {
            this._addToMasonryLayout();
          }
        });
    } else {
      if (
        type.actionData &&
        type.actionData.type &&
        type.actionData.type === 'NEXT_PAGE'
      ) {
        this.page++;
      } else if (type.actionData.type === 'BEFORE_PAGE') {
        this.page--;
      } else {
        this.page = +type.actionName;
      }
      // Pasek numerów stron bierze bieżącą stronę stąd, a nie z tekstu klikniętego elementu —
      // strzałki mogą mieć dowolną zawartość (tekst, obraz), a o kierunku decyduje ich akcja.
      this.globalService.selectPagePaginator = this.page;
      this.paginatorOn = true;
      const url = this.router
        .createUrlTree([], {
          relativeTo: this.route,
          queryParams: {pageNumber: this.page, typeView: this.activeTab},
          queryParamsHandling: 'merge',
        })
        .toString();
      this.location.go(url);
      this.loading.next(true);
      focusBody();
      this.typePaginator = true;
      const requestParams = this.getListRequestParams(this.queryParams);
      this.filtersService.paramsSelectFiters = requestParams;
      const tableParams = this.getTableParamsForListRequest(requestParams);
      this.subSink.sink = this.apiService
        .getList(tableParams)
        .subscribe((data: any) => {
          const numPageLength = data?.elements[0]?.data.length
            ? data?.elements[0]?.data.length
            : 0;
          if (numPageLength > 0) {
            this.createStyles(data.elements);
            // Wywołanie po zebraniu fragmentów CSS przez createStyles —
            // afterGetData poniżej dokłada własną porcję i uruchamia
            // styleService.setStyles() jeszcze raz w ramach swojego przebiegu,
            // ale to spójne z resztą flow: setStyles zawsze odbywa się
            // po zbatchowaniu wszystkich fragmentów pojedynczej odpowiedzi.
            this.styleService.setStyles();
            const {scrollTopIdElement} = this.getCurrentListParams();
            this.updateStructure(data?.elements);
            this.loading.next(false);
            this.refreshDivPaginator();
            this.afterGetData(data, scrollTopIdElement);
            const urlBack = this.router
              .createUrlTree([], {
                relativeTo: this.route,
                queryParams: {
                  pageNumber: this.page,
                  typeView: this.activeTab,
                },
                queryParamsHandling: 'merge',
              })
              .toString();
            this.location.go(urlBack);
          }
          if (this.isMasonryList) {
            this._generateMasonryLayout();
          }
        });
    }
  }

  private changePaginationVisibility(): void {
    this._showPagination = this.page * this.settings.elementsPerPage < this.allItemsCount;
  }

  private getCurrentListParams() {
    const currentParams = {
      page: 1,
      scrollTopIdElement: null,
      data: null,
    };
    if (!this.isListPageType) {
      return currentParams;
    }
    if (this.isRouteBack) {
      const listParams = this.localStorageService.getItem('list_params');

      if (listParams) {
        const listParamsArr = JSON.parse(listParams) as ListParams[];
        const thisPageParams = listParamsArr.find(
          (params) => params.path === this.listPathName,
        );
        if (thisPageParams) {
          currentParams.scrollTopIdElement = thisPageParams.scrollTopIdElement;
          currentParams.data = thisPageParams.data;
        }
      }
    }

    this.deleteCurrentListParams();
    return currentParams;
  }

  private setCurrentListParams(): void {
    if (!this.isListPageType) {
      return;
    }
    const listParams = this.localStorageService.getItem('list_params');
    const listParamsArray = (
      listParams ? JSON.parse(listParams) : []
    ) as ListParams[];
    const paginatedStructure = chunkArray(
      this.structure || [],
      this.settings.elementsPerPage,
    );
    const lastPage = paginatedStructure[this.page - 1];
    listParamsArray.push({
      path: this.listPathName,
      page: this.page,
      scrollTopIdElement: this.scrollTopIdElement,
      data: {
        allCount: this.allItemsCount,
        elements: lastPage,
        filters: this.filters,
        noResults: this.noResults,
        pagination: this.pagination,
        settings: this.settings,
        sorting: this.sorting,
        maxYearOfCreation: null,
        minYearOfCreation: null,
        minPrice: null,
        maxPrice: null,
        header: this.header,
      },
    });

    // get only 5 last elements
    if (listParamsArray.length > 3) {
      listParamsArray.splice(0, listParamsArray.length - 3);
    }

    const filteredListParamsArray = this.filterCircularReferences(listParamsArray);
    this.localStorageService.setItem(
      'list_params',
      JSON.stringify(filteredListParamsArray),
    );
  }

  private filterCircularReferences(obj: ListParams[]) {
    const seen = new WeakSet();
    return JSON.parse(
      JSON.stringify(obj, (key, value) => {
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            return undefined;
          }
          seen.add(value);
        }
        return value;
      }),
    );
  }

  private deleteCurrentListParams(): void {
    this.localStorageService.removeItem('isRouteBack');
    const listParams = this.localStorageService.getItem('list_params');

    if (listParams) {
      const listParamsArr = JSON.parse(listParams) as ListParams[];
      const index = listParamsArr.findIndex(
        (params) => params.path === this.listPathName,
      );

      if (index > -1) {
        listParamsArr.splice(index, 1);
        this.localStorageService.setItem(
          'list_params',
          JSON.stringify(listParamsArr),
        );
      }
    }
  }

  private getFirstVisibleElementOnScreen() {
    if (isPlatformBrowser(this.platformId)) {
      const elements = this._allListDivs;
      if (!elements) {
        return null;
      }
      let firstVisible = null;
      elements.forEach((element) => {
        if (!firstVisible && isFullyVisible(element)) {
          firstVisible = element;
        }
      });
      return firstVisible;
    }
    return null;
  }

  private updateList(params: any): void {
    // Set loading state to disable filter interactions during data fetch
    ListDetailsService.setLoading(true);

    const requestParams = this.getListRequestParams(params);

    // Build table parameters for API request
    const tableParams = this.getTableParamsForListRequest(requestParams);
    this.filtersService.paramsSelectFiters = requestParams;

    this.subSink.sink = this.apiService
      .getList(tableParams)
      .pipe(take(1))
      .subscribe({
        next: (list) => {
          this.afterGetData(list);
        },
        error: () => {
          // Ustaw stan ładowania na false w przypadku błędu
          ListDetailsService.setLoading(false);
        },
      });
  }

  private updateListAfterAdvancedFiltersChanged(params: any): void {
    // Set loading state to disable filter interactions during data fetch
    ListDetailsService.setLoading(true);

    // Build table parameters for API request
    const tableParams = this.getTableParamsWithoutBListBaseParams(params);
    this.filtersService.paramsSelectFiters = params;

    this.subSink.sink = this.apiService
      .getList(tableParams)
      .pipe(take(1))
      .subscribe({
        next: (list) => {
          this.afterGetData(list);
        },
        error: () => {
          // Ustaw stan ładowania na false w przypadku błędu
          ListDetailsService.setLoading(false);
        },
      });
  }

  private getListRequestParams(params: Params | null = {}): Params {
    return this.getQueryParamsWithoutUnnecessaryFields({
      ...(params || {}),
      ...this.advancedFilterParams,
    });
  }

  private getTableParamsForListRequest(params: Params): TableParams {
    return Object.keys(this.advancedFilterParams).length
      ? this.getTableParamsWithoutBListBaseParams(params)
      : this.getTableParams(params);
  }

  private initializeFilterChanging(): void {
    if (this.filtersService) {
      this.subSink.sink = this.filtersService.filtersToChanged
        .pipe(filter(Boolean))
        .subscribe({
          next: (_filter) => {
            this.page = 1;
            this.goTo(_filter);
          },
        });
    }
  }

  private goTo(params: Params | null): void {
    const oldParams =
      params && Object.keys(params).length === 0
        ? this.specialBlock.settings.queryParams
        : params;

    const finalParams = this.getQueryParamsWithoutUnnecessaryFields(oldParams);

    this.router.navigate(['./'], {
      relativeTo: this.route,
      fragment: 'no-scroll',
      queryParams: finalParams,
      // wewnętrzna nawigacja listy nie może resetować filtrów zaawansowanych
      state: {keepAdvancedFilters: true},
    });
  }

  private getQueryParamsWithoutUnnecessaryFields(params: Params) {
    if (!params || !Object.keys(params).length) {
      return {};
    }
    const newParams = {};

    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (key !== FilterType.AVAILABILITY) {
          value = value.filter((val) => +val !== -1);
        }

        if (Array.isArray(value) && value.length) {
          newParams[key] = value;
        }
      } else {
        newParams[key] = value;
      }
    });

    return newParams;
  }

  private distributeElementsToColumns(array: any, numberOfColumns: number) {
    // Initialize columns as an array of empty arrays
    const columns = [];
    for (let n = 0; n < numberOfColumns; n++) {
      columns.push([]);
    }

    // Iterate over the array elements
    for (let i = 0; i < array.length; i++) {
      // Use the modulus operator to determine the column
      columns[i % numberOfColumns].push(array[i]);
    }

    return columns;
  }

  private _generateMasonryLayout(): void {
    if (!this.listContainer) {
      return;
    }
    const {gridTemplateColumns} = this.currentViewBlockStyle;
    const numberOfColumns = gridTemplateColumns.split(' ').length;
    this.structure.forEach((s) => {
      this.masonryList[s.viewName] = this.distributeElementsToColumns(
        s.data,
        numberOfColumns,
      );
    });
  }

  private _addToMasonryLayout(): void {
    if (!this.listContainer) {
      return;
    }
    const {gridTemplateColumns} = this.currentViewBlockStyle;
    const numberOfColumns = gridTemplateColumns.split(' ').length;
    this.structure.forEach((s) => {
      const columns = this.distributeElementsToColumns(s.data, numberOfColumns);
      columns.forEach((column, i) => {
        const currentColumnLength = this.masonryList[s.viewName][i].length;
        if (currentColumnLength < column.length) {
          this.masonryList[s.viewName][i].push(
            ...column.slice(currentColumnLength),
          );
        }
      });
    });
  }

  removeSpecificParams(viewName: string): void {
    // Skopiuj aktualne parametry z URL
    const params = {...this.route.snapshot.queryParams};

    // Sprawdź i zaktualizuj globalne parametry wyszukiwania
    const typeView = params['typeView'];
    if (
      typeView &&
      Object.prototype.hasOwnProperty.call(this.globalService.paramsGlobalSearch, typeView)
    ) {
      this.globalService.paramsGlobalSearch[typeView] = {
        pageNumber: +params['pageNumber'] || 1,
        typeView,
      };
    }

    // Usuń niepotrzebne parametry z URL
    ['pageNumber', 'typeView'].forEach((param) => delete params[param]);

    // Dodaj nowe parametry dla podanego widoku
    if (Object.prototype.hasOwnProperty.call(this.globalService.paramsGlobalSearch, viewName)) {
      const {pageNumber, typeView} =
        this.globalService.paramsGlobalSearch[viewName];
      params['pageNumber'] = pageNumber;
      params['typeView'] = typeView;
    }

    // Nawiguj z zaktualizowanymi parametrami
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      queryParamsHandling: 'merge',
    });
  }
}
